import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  User, 
  Stethoscope, 
  Shield, 
  Building, 
  FileText,
  CheckCircle,
  Upload,
  X,
  Plus,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { ipdApi } from '@/api/ipd';
import { IPDAdmissionData, IPDQueueEntry, Doctor, Ward, WardSubType, Bed, InsuranceCompany, IPDPackageType } from '@/types/ipd';
import { IPD_PACKAGE_TYPE_OPTIONS } from '@/lib/ipdPackageTypes';
import { maskPhoneDisplay } from '@/utils/stringUtils';
import { DoctorSearchableSelect } from '@/components/common/DoctorSearchableSelect';
import { MEDICAL_SPECIALISATIONS } from '@/lib/medicalSpecialisations';
import {
  findSpecialisationForSurgery,
  getSurgeriesForSpecialisation,
  loadCustomPackageSpecialisations,
  loadCustomSurgeriesBySpec,
  orderPackageSpecialisations,
  persistCustomPackageSpecialisations,
  persistCustomSurgeriesForSpec,
} from '@/lib/surgeriesBySpecialisation';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select';
import { useAuth } from '@/contexts/AuthContext';
import { formatIstDatetimeLocal, parseIstDatetimeLocalToIso } from '@/lib/istDatetimeLocal';
import { LeadSourceSearchableSelect } from '@/components/common/LeadSourceSearchableSelect';
import { leadSourceNeedsDetails } from '@/lib/leadSources';
import { patientNeedsRegistrationSourceCapture } from '@/lib/patientRegistrationSource';
import { leadsApi } from '@/api/leads';
import { useQuery } from '@tanstack/react-query';
import type { IPDAdmission } from '@/types/ipd';

interface IPDAdmissionFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  queueEntry: IPDQueueEntry | null;
  /** When set, opens the form in edit mode for an existing admission (admin / super admin). */
  editAdmissionId?: string | null;
}

export default function IPDAdmissionForm({ 
  isOpen, 
  onClose, 
  onSuccess, 
  queueEntry,
  editAdmissionId = null,
}: IPDAdmissionFormProps) {
  const { user } = useAuth();
  const isEditMode = Boolean(editAdmissionId);
  const canSetAdmissionDateTime =
    user?.role === 'HOSPITAL_ADMIN' || user?.role === 'SUPER_ADMIN';

  const { data: leadFilterOptions } = useQuery({
    queryKey: ['lead-filter-options', user?.hospitalId],
    queryFn: () => leadsApi.getLeadFilterOptions(),
    enabled: isOpen && !!user?.hospitalId,
  });

  const leadSourceApiSources = leadFilterOptions?.sources;

  const SALES_DROPDOWN_STORAGE_KEY = 'thms:ipd:salesPersons';
  const DEFAULT_SALES_NAMES = ['Mohit', 'Nawaz', 'Tanish'];
  const PARTNER_DROPDOWN_STORAGE_KEY = 'thms:ipd:partnerLeads';
  const DEFAULT_PARTNER_NAMES = ['Surgenix', 'Carelan', 'Hexa', 'Saarthi', 'Mediedge'];
  const FINANCING_PARTNER_DROPDOWN_STORAGE_KEY = 'thms:ipd:financingPartners';
  const DEFAULT_FINANCING_PARTNER_NAMES = ['Claimfriendy'];
  const THIRD_PARTY_HOSPITAL_DROPDOWN_STORAGE_KEY = 'thms:ipd:thirdPartyHospitals';
  const REFERRER_DROPDOWN_STORAGE_KEY = 'thms:ipd:referrers';
  const [formData, setFormData] = useState<IPDAdmissionData>({
    queueId: '',
    assignedDoctorId: '',
    insuranceType: 'NA' as any,
    wardType: 'GENERAL' as any,
    totalPackageCost: '',
    surgeryName: '',
    packageType: 'PACKAGE_1' as IPDPackageType,
    gstApplicable: false,
  });
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [wards, setWards] = useState<Ward[]>([]);
  const [beds, setBeds] = useState<Bed[]>([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState<InsuranceCompany[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedWard, setSelectedWard] = useState<Ward | null>(null);
  const [insuranceCardFile, setInsuranceCardFile] = useState<File | null>(null);
  const [addDoctorOpen, setAddDoctorOpen] = useState(false);
  const [newDoctorName, setNewDoctorName] = useState('');
  const [newDoctorSpec, setNewDoctorSpec] = useState<string>('');
  const [creatingDoctor, setCreatingDoctor] = useState(false);
  const [salesOptions, setSalesOptions] = useState<string[]>(DEFAULT_SALES_NAMES);

  const addSalesOption = (name: string | null | undefined) => {
    const nm = String(name || '').trim();
    if (!nm) return;
    setSalesOptions((prev) => (prev.includes(nm) ? prev : [...prev, nm]));
  };
  const [addingSalesName, setAddingSalesName] = useState(false);
  const [newSalesName, setNewSalesName] = useState('');
  const [partnerOptions, setPartnerOptions] = useState<string[]>(DEFAULT_PARTNER_NAMES);
  const [addingPartnerName, setAddingPartnerName] = useState(false);
  const [newPartnerName, setNewPartnerName] = useState('');
  const [financingPartnerOptions, setFinancingPartnerOptions] = useState<string[]>(
    DEFAULT_FINANCING_PARTNER_NAMES,
  );
  const [addingFinancingPartnerName, setAddingFinancingPartnerName] = useState(false);
  const [newFinancingPartnerName, setNewFinancingPartnerName] = useState('');
  const [thirdPartyHospitalOptions, setThirdPartyHospitalOptions] = useState<string[]>([]);
  const [addingThirdPartyHospitalName, setAddingThirdPartyHospitalName] = useState(false);
  const [newThirdPartyHospitalName, setNewThirdPartyHospitalName] = useState('');
  const [customReferrerOptions, setCustomReferrerOptions] = useState<string[]>([]);
  const [addingReferrerName, setAddingReferrerName] = useState(false);
  const [newReferrerName, setNewReferrerName] = useState('');
  const [packageSurgeryRows, setPackageSurgeryRows] = useState<
    Array<{ key: string; specialisation: string; surgeryName: string }>
  >([{ key: 'row-0', specialisation: '', surgeryName: '' }]);
  const [customPackageSpecialisations, setCustomPackageSpecialisations] = useState<string[]>([]);
  const [customSurgeriesBySpec, setCustomSurgeriesBySpec] = useState<Record<string, string[]>>({});
  const [addingPackageSpecialisationForKey, setAddingPackageSpecialisationForKey] = useState<string | null>(null);
  const [newPackageSpecialisation, setNewPackageSpecialisation] = useState('');
  const [addingPackageSurgeryForKey, setAddingPackageSurgeryForKey] = useState<string | null>(null);
  const [newPackageSurgery, setNewPackageSurgery] = useState('');
  const [admissionDateTime, setAdmissionDateTime] = useState('');
  const [originalAdmissionDateTime, setOriginalAdmissionDateTime] = useState('');
  const [loadingAdmission, setLoadingAdmission] = useState(false);

  const newPackageSurgeryRowKey = () =>
    `row-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

  // Reset form when modal opens for new admission
  useEffect(() => {
    if (isOpen && queueEntry && !editAdmissionId) {
      setFormData({
        queueId: queueEntry.id,
        assignedDoctorId: '',
        insuranceType: 'NA' as any,
        wardType: 'GENERAL' as any,
        wardSubType: undefined,
        wardId: '',
        bedId: '',
        insuranceCompany: '',
        policyNumber: '',
        insuranceNumber: '',
        tpaName: '',
        roomNumber: '',
        bedNumber: '',
        chiefComplaint: '',
        admissionNotes: '',
        referralPersonName: queueEntry.patient?.referralPersonName || '',
        partnerLeadName: '',
        financingPartnerName: '',
        thirdPartyHospitalName: '',
        admissionCareType: '',
        salesPersonName: '',
        leadSource: '',
        leadSourceDetails: '',
        totalPackageCost: '',
        surgeryName: '',
        packageType: 'PACKAGE_1',
        gstApplicable: false,
      });
      setInsuranceCardFile(null);
      setAdmissionDateTime('');
      setOriginalAdmissionDateTime('');
      setPackageSurgeryRows([{ key: newPackageSurgeryRowKey(), specialisation: '', surgeryName: '' }]);
    }
  }, [isOpen, queueEntry, editAdmissionId]);

  useEffect(() => {
    if (!isOpen) return;
    setCustomPackageSpecialisations(loadCustomPackageSpecialisations());
    setCustomSurgeriesBySpec(loadCustomSurgeriesBySpec());
  }, [isOpen]);

  const packageSpecialisationOptions = useMemo(() => {
    const specs = orderPackageSpecialisations(customPackageSpecialisations);
    return [
      ...specs.map((s) => ({ value: s, label: s, searchText: s })),
      { value: '__add__', label: '+ Add specialisation…', searchText: 'add specialisation' },
    ];
  }, [customPackageSpecialisations]);

  const surgeryOptionsForSpec = (specialisation: string) => {
    if (!specialisation) return [];
    const surgeries = getSurgeriesForSpecialisation(specialisation, customSurgeriesBySpec);
    return [
      ...surgeries.map((s) => ({ value: s, label: s, searchText: s })),
      { value: '__add__', label: '+ Add surgery…', searchText: 'add surgery' },
    ];
  };

  // Load existing admission for edit mode
  useEffect(() => {
    if (!isOpen || !editAdmissionId || !queueEntry) return;
    let cancelled = false;

    const loadAdmission = async () => {
      setLoadingAdmission(true);
      try {
        const res = await ipdApi.getAdmissionById(editAdmissionId);
        const adm = res.data?.data as IPDAdmission & {
          bed?: { id: string; bedNumber: string; wardId: string; ward?: Ward };
          insuranceNumber?: string | null;
          salesPersonName?: string | null;
          partnerLeadName?: string | null;
          financingPartnerName?: string | null;
          thirdPartyHospitalName?: string | null;
          admissionCareType?: string | null;
          leadSource?: string | null;
          leadSourceDetails?: string | null;
        };
        if (!adm || cancelled) return;

        const dtLocal = formatIstDatetimeLocal(adm.admissionDate);
        setAdmissionDateTime(dtLocal);
        setOriginalAdmissionDateTime(dtLocal);

        const customSpecs = loadCustomPackageSpecialisations();
        const customSurgeries = loadCustomSurgeriesBySpec();
        setCustomPackageSpecialisations(customSpecs);
        setCustomSurgeriesBySpec(customSurgeries);

        const surgeryNm = adm.surgeryName || '';
        const specFromAdm = (adm as { surgerySpecialisation?: string | null }).surgerySpecialisation;
        const packageFromApi = Array.isArray(adm.packageSurgeries) ? adm.packageSurgeries : [];
        if (packageFromApi.length > 0) {
          setPackageSurgeryRows(
            packageFromApi.map((s, i) => ({
              key: s.id || `loaded-${i}`,
              specialisation: (s.specialisation || '').trim(),
              surgeryName: (s.surgeryName || '').trim(),
            }))
          );
        } else if (surgeryNm || specFromAdm) {
          setPackageSurgeryRows([
            {
              key: newPackageSurgeryRowKey(),
              specialisation:
                (specFromAdm || '').trim() ||
                findSpecialisationForSurgery(surgeryNm, customSurgeries),
              surgeryName: surgeryNm,
            },
          ]);
        } else {
          setPackageSurgeryRows([{ key: newPackageSurgeryRowKey(), specialisation: '', surgeryName: '' }]);
        }

        const wardIdFromBed = adm.bed?.wardId ?? adm.bed?.ward?.id ?? '';

        let leadSource = adm.leadSource || '';
        let leadSourceDetails = adm.leadSourceDetails || '';
        let salesPersonName = adm.salesPersonName || '';

        const needsCrmLeadSource = !leadSource.trim();
        const needsCrmSalesPerson = !salesPersonName.trim();
        if (needsCrmLeadSource || needsCrmSalesPerson) {
          try {
            const defaultsRes = await ipdApi.getAdmissionFormDefaults(
              queueEntry.id,
              adm.admissionDate
            );
            const defaults = defaultsRes.data?.data;
            if (defaults) {
              if (needsCrmLeadSource && (defaults.crmLeadSourceRaw || defaults.leadSource)) {
                leadSource = defaults.crmLeadSourceRaw || defaults.leadSource || '';
                leadSourceDetails = defaults.leadSourceDetails || '';
              }
              if (needsCrmSalesPerson && defaults.salesPersonName) {
                salesPersonName = defaults.salesPersonName;
                addSalesOption(defaults.salesPersonName);
              }
            }
          } catch {
            // Best-effort CRM prefill when admission predates leadSource field
          }
        }

        setFormData({
          queueId: queueEntry.id,
          assignedDoctorId: adm.assignedDoctorId || adm.assignedDoctor?.id || '',
          insuranceType: adm.insuranceType,
          insuranceCompany: adm.insuranceCompany || '',
          policyNumber: adm.policyNumber || '',
          insuranceNumber: (adm as { insuranceNumber?: string }).insuranceNumber || '',
          tpaName: adm.tpaName || '',
          wardType: adm.wardType,
          wardSubType: adm.wardSubType,
          wardId: wardIdFromBed,
          bedId: adm.bedId || adm.bed?.id || '',
          roomNumber: adm.roomNumber || adm.bed?.ward?.name || '',
          bedNumber: adm.bedNumber || adm.bed?.bedNumber || '',
          chiefComplaint: adm.chiefComplaint || '',
          admissionNotes: adm.admissionNotes || '',
          referralPersonName:
            adm.queue?.patient?.referralPersonName ||
            queueEntry.patient?.referralPersonName ||
            '',
          partnerLeadName: adm.partnerLeadName || '',
          financingPartnerName: adm.financingPartnerName || '',
          thirdPartyHospitalName: adm.thirdPartyHospitalName || '',
          admissionCareType: (adm.admissionCareType as 'SURGERY' | 'MEDICAL_MANAGEMENT' | null) || '',
          salesPersonName,
          leadSource,
          leadSourceDetails,
          totalPackageCost: adm.totalPackageCost != null ? String(adm.totalPackageCost) : '',
          surgeryName: adm.surgeryName || '',
          packageType: adm.packageType || 'PACKAGE_1',
          gstApplicable: !!adm.gstApplicable,
        });

        if (wardIdFromBed) {
          try {
            const wardsRes = await ipdApi.getWards();
            const wardList = wardsRes.data?.data ?? [];
            setWards(wardList);
            const ward = wardList.find((w: Ward) => w.id === wardIdFromBed);
            if (ward) {
              setSelectedWard(ward);
              const bedsRes = await ipdApi.getAvailableBeds(wardIdFromBed);
              setBeds(bedsRes.data?.data ?? []);
            }
          } catch {
            // Ward/bed prefill is best-effort
          }
        }
      } catch (error) {
        console.error('Error loading admission for edit:', error);
        toast.error('Failed to load admission details');
      } finally {
        if (!cancelled) setLoadingAdmission(false);
      }
    };

    void loadAdmission();
    return () => {
      cancelled = true;
    };
  }, [isOpen, editAdmissionId, queueEntry?.id]);

  // Load persisted sales person names
  useEffect(() => {
    if (!isOpen) return;
    try {
      const raw = localStorage.getItem(SALES_DROPDOWN_STORAGE_KEY) || '[]';
      const parsed = JSON.parse(raw) as unknown;
      const extra = Array.isArray(parsed)
        ? parsed.map((x) => String(x || '').trim()).filter(Boolean)
        : [];
      const merged = Array.from(new Set([...DEFAULT_SALES_NAMES, ...extra]));
      setSalesOptions(merged);
    } catch {
      setSalesOptions(DEFAULT_SALES_NAMES);
    }
  }, [isOpen]);

  // Load persisted partner leads
  useEffect(() => {
    if (!isOpen) return;
    try {
      const raw = localStorage.getItem(PARTNER_DROPDOWN_STORAGE_KEY) || '[]';
      const parsed = JSON.parse(raw) as unknown;
      const extra = Array.isArray(parsed)
        ? parsed.map((x) => String(x || '').trim()).filter(Boolean)
        : [];
      const merged = Array.from(new Set([...DEFAULT_PARTNER_NAMES, ...extra]));
      setPartnerOptions(merged);
    } catch {
      setPartnerOptions(DEFAULT_PARTNER_NAMES);
    }
  }, [isOpen]);

  // Load persisted financing partners
  useEffect(() => {
    if (!isOpen) return;
    try {
      const raw = localStorage.getItem(FINANCING_PARTNER_DROPDOWN_STORAGE_KEY) || '[]';
      const parsed = JSON.parse(raw) as unknown;
      const extra = Array.isArray(parsed)
        ? parsed.map((x) => String(x || '').trim()).filter(Boolean)
        : [];
      const merged = Array.from(new Set([...DEFAULT_FINANCING_PARTNER_NAMES, ...extra]));
      setFinancingPartnerOptions(merged);
    } catch {
      setFinancingPartnerOptions(DEFAULT_FINANCING_PARTNER_NAMES);
    }
  }, [isOpen]);

  // Load persisted third-party hospitals
  useEffect(() => {
    if (!isOpen) return;
    try {
      const raw = localStorage.getItem(THIRD_PARTY_HOSPITAL_DROPDOWN_STORAGE_KEY) || '[]';
      const parsed = JSON.parse(raw) as unknown;
      const extra = Array.isArray(parsed)
        ? parsed.map((x) => String(x || '').trim()).filter(Boolean)
        : [];
      setThirdPartyHospitalOptions(Array.from(new Set(extra)));
    } catch {
      setThirdPartyHospitalOptions([]);
    }
  }, [isOpen]);

  // Load persisted custom referrers
  useEffect(() => {
    if (!isOpen) return;
    try {
      const raw = localStorage.getItem(REFERRER_DROPDOWN_STORAGE_KEY) || '[]';
      const parsed = JSON.parse(raw) as unknown;
      const extra = Array.isArray(parsed)
        ? parsed.map((x) => String(x || '').trim()).filter(Boolean)
        : [];
      setCustomReferrerOptions(Array.from(new Set(extra)));
    } catch {
      setCustomReferrerOptions([]);
    }
  }, [isOpen]);

  // Prefill sales person + lead source from CRM / patient registration (new admissions only)
  useEffect(() => {
    if (!isOpen || editAdmissionId || !queueEntry?.id) return;
    let cancelled = false;

    const loadDefaults = async () => {
      try {
        const res = await ipdApi.getAdmissionFormDefaults(queueEntry.id);
        if (cancelled) return;
        const defaults = res.data?.data;
        if (!defaults) return;

        const crmSalesPerson = defaults.salesPersonName?.trim() || '';
        if (crmSalesPerson) addSalesOption(crmSalesPerson);

        setFormData((prev) => ({
          ...prev,
          salesPersonName: prev.salesPersonName?.trim()
            ? prev.salesPersonName
            : crmSalesPerson,
          leadSource: prev.leadSource?.trim()
            ? prev.leadSource
            : defaults.crmLeadSourceRaw ||
              defaults.leadSource ||
              queueEntry.patient?.registrationSource ||
              '',
          leadSourceDetails: prev.leadSourceDetails?.trim()
            ? prev.leadSourceDetails
            : defaults.leadSourceDetails ||
              queueEntry.patient?.registrationSourceDetails ||
              '',
        }));
      } catch {
        // Best-effort prefill from patient record when API fails
        if (cancelled) return;
        setFormData((prev) => ({
          ...prev,
          leadSource: prev.leadSource?.trim()
            ? prev.leadSource
            : queueEntry.patient?.registrationSource || '',
          leadSourceDetails: prev.leadSourceDetails?.trim()
            ? prev.leadSourceDetails
            : queueEntry.patient?.registrationSourceDetails || '',
        }));
      }
    };

    void loadDefaults();
    return () => {
      cancelled = true;
    };
  }, [isOpen, editAdmissionId, queueEntry?.id, queueEntry?.patient?.registrationSource]);

  // Fetch required data
  useEffect(() => {
    if (isOpen) {
      fetchDoctors();
      fetchWards();
      fetchInsuranceCompanies();
    }
  }, [isOpen]);

  const fetchDoctors = async () => {
    try {
      const response = await ipdApi.getDoctors();
      setDoctors(response.data.data);
    } catch (error) {
      console.error('Error fetching doctors:', error);
      toast.error('Failed to fetch doctors');
    }
  };

  const referrerOptions: SearchableSelectOption[] = useMemo(
    () => [
      ...doctors.map((d) => {
        const label = d.specialisation ? `Dr ${d.name} (${d.specialisation})` : `Dr ${d.name}`;
        return {
          value: label,
          label,
          searchText: [d.name, d.specialisation, label, `Dr ${d.name}`].filter(Boolean).join(' '),
        };
      }),
      ...customReferrerOptions.map((name) => ({
        value: name,
        label: name,
        searchText: name,
      })),
      { value: '__add__', label: '+ Add referrer…', searchText: 'add referrer' },
    ],
    [doctors, customReferrerOptions]
  );

  // Map legacy stored plain doctor names (without Dr/spec) onto the display option value.
  const referrerSelectValue = useMemo(() => {
    const raw = String(formData.referralPersonName || '').trim();
    if (!raw) return '';
    if (referrerOptions.some((o) => o.value === raw)) return raw;
    const byName = doctors.find(
      (d) =>
        d.name.trim().toLowerCase() === raw.toLowerCase() ||
        `dr ${d.name}`.toLowerCase() === raw.toLowerCase()
    );
    if (byName) {
      return byName.specialisation
        ? `Dr ${byName.name} (${byName.specialisation})`
        : `Dr ${byName.name}`;
    }
    return raw;
  }, [formData.referralPersonName, referrerOptions, doctors]);

  const fetchWards = async () => {
    try {
      const response = await ipdApi.getWards();
      setWards(response.data.data);
    } catch (error) {
      console.error('Error fetching wards:', error);
      toast.error('Failed to fetch wards');
    }
  };

  const fetchInsuranceCompanies = async () => {
    try {
      const response = await ipdApi.getInsuranceCompanies();
      setInsuranceCompanies(response.data.data);
    } catch (error) {
      console.error('Error fetching insurance companies:', error);
      toast.error('Failed to fetch insurance companies');
    }
  };

  const fetchBeds = async (wardId: string) => {
    try {
      const response = await ipdApi.getAvailableBeds(wardId);
      setBeds(response.data.data);
    } catch (error) {
      console.error('Error fetching beds:', error);
      toast.error('Failed to fetch beds');
    }
  };

  const handleQuickCreateDoctor = async () => {
    const nm = newDoctorName.trim();
    if (!nm) {
      toast.error('Doctor name is required');
      return;
    }
    setCreatingDoctor(true);
    try {
      const resp = await ipdApi.quickCreateDoctor({
        name: nm,
        specialisation: newDoctorSpec || undefined,
      });
      const created = resp.data?.data;
      toast.success('Doctor added');
      setAddDoctorOpen(false);
      setNewDoctorName('');
      setNewDoctorSpec('');
      await fetchDoctors();
      if (created?.id) {
        handleInputChange('assignedDoctorId', created.id);
      }
    } catch (error: any) {
      console.error('Error creating doctor:', error);
      toast.error(error.response?.data?.message || 'Failed to add doctor');
    } finally {
      setCreatingDoctor(false);
    }
  };

  const handleInputChange = (
    field: keyof IPDAdmissionData,
    value: string | number | boolean | WardSubType | undefined
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Third-party hospital stay: no ward/bed at this hospital
    if (field === 'thirdPartyHospitalName' && value) {
      setFormData((prev) => ({
        ...prev,
        thirdPartyHospitalName: String(value),
        wardId: '',
        bedId: '',
        bedNumber: '',
        roomNumber: '',
        wardSubType: undefined,
        wardType: 'PRIVATE' as any,
      }));
      setSelectedWard(null);
      setBeds([]);
      return;
    }

    // Reset ward and bed selection when ward type or sub type changes
    if (field === 'wardType' || field === 'wardSubType') {
      setFormData(prev => ({
        ...prev,
        wardId: '',
        bedId: '',
        bedNumber: '',
        roomNumber: ''
      }));
      setSelectedWard(null);
      setBeds([]);
    }

    // If ward is selected, fetch beds for that ward and set ward name
    if (field === 'wardId' && value) {
      const ward = wards.find(w => w.id === value);
      if (ward) {
        setSelectedWard(ward);
        // Auto-set ward name in form data
        setFormData(prev => ({
          ...prev,
          roomNumber: ward.name
        }));
        fetchBeds(String(value));
      }
    }
  };

  const isThirdPartyStay = Boolean(String(formData.thirdPartyHospitalName || '').trim());
  const needsRegistrationSourceCapture =
    !isEditMode && patientNeedsRegistrationSourceCapture(queueEntry?.patient);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const wardRequired = !isEditMode && !isThirdPartyStay;
    if (!formData.assignedDoctorId || (wardRequired && (!formData.wardType || !formData.wardId))) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (needsRegistrationSourceCapture && !formData.leadSource?.trim()) {
      toast.error('Please select how this self-registered patient heard about the hospital.');
      return;
    }
    if (
      needsRegistrationSourceCapture &&
      leadSourceNeedsDetails(formData.leadSource) &&
      !formData.leadSourceDetails?.trim()
    ) {
      toast.error('Please enter lead source details.');
      return;
    }

    setIsSubmitting(true);
    try {
      const formDataToSend = new FormData();
      
      Object.entries(formData).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (key === 'gstApplicable') {
          formDataToSend.append(key, value ? 'true' : 'false');
          return;
        }
        if (
          key === 'surgeryName' ||
          key === 'surgerySpecialisation' ||
          key === 'packageSurgeries' ||
          key === 'referralPersonName'
        ) {
          return;
        }
        formDataToSend.append(key, value.toString());
      });
      // Always send referrer (including empty) so admin can set or clear it on edit.
      formDataToSend.append(
        'referralPersonName',
        String(referrerSelectValue || formData.referralPersonName || '').trim()
      );

      const filledSurgeries = packageSurgeryRows
        .map((r) => ({
          specialisation: r.specialisation.trim() || null,
          surgeryName: r.surgeryName.trim(),
        }))
        .filter((r) => r.surgeryName);
      formDataToSend.append('packageSurgeries', JSON.stringify(filledSurgeries));
      if (filledSurgeries[0]) {
        formDataToSend.append('surgeryName', filledSurgeries[0].surgeryName);
        if (filledSurgeries[0].specialisation) {
          formDataToSend.append('surgerySpecialisation', filledSurgeries[0].specialisation);
        }
      }

      if (canSetAdmissionDateTime && admissionDateTime) {
        const changed = admissionDateTime !== originalAdmissionDateTime;
        if (isEditMode) {
          if (changed) {
            const iso = parseIstDatetimeLocalToIso(admissionDateTime);
            if (iso) formDataToSend.append('admissionDate', iso);
          }
        } else {
          const iso = parseIstDatetimeLocalToIso(admissionDateTime);
          if (iso) formDataToSend.append('admissionDate', iso);
        }
      }
      
      if (insuranceCardFile) {
        formDataToSend.append('insuranceCard', insuranceCardFile);
      }

      if (isEditMode && editAdmissionId) {
        await ipdApi.updateAdmission(editAdmissionId, formDataToSend);
        toast.success('Admission updated successfully');
        onSuccess();
        onClose();
        return;
      }
      
      const admissionRes = await ipdApi.createAdmission(formDataToSend);
      const admissionId = admissionRes?.data?.data?.id as string | undefined;

      if (admissionId) {
        try {
          const pdfRes = await ipdApi.downloadAdmissionFormPDF(admissionId);
          const blob = new Blob([pdfRes.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const w = window.open(url, '_blank');
          if (w) {
            w.onload = () => {
              try {
                w.focus();
                w.print();
              } finally {
                window.URL.revokeObjectURL(url);
              }
            };
          } else {
            window.URL.revokeObjectURL(url);
            toast.info('Patient admitted. Allow pop-ups to auto-open print form.');
          }
        } catch (printErr) {
          console.error('Auto print admission form failed:', printErr);
          toast.info('Patient admitted. You can print admission form from the admitted list.');
        }
      }

      toast.success('Patient admitted successfully!');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error(isEditMode ? 'Error updating admission:' : 'Error creating admission:', error);
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      const message = isTimeout
        ? 'Request timed out. The server may be slow. Please try again or contact support.'
        : (error.response?.data?.message || (isEditMode ? 'Failed to update admission' : 'Failed to admit patient'));
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getWardTypeColor = (type: string) => {
    switch (type) {
      case 'ICU': return 'bg-red-100 text-red-800 border-red-200';
      case 'PRIVATE': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'SEMI_PRIVATE': return 'bg-green-100 text-green-800 border-green-200';
      case 'EMERGENCY': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getInsuranceTypeColor = (type: string) => {
    switch (type) {
      case 'CASHLESS': return 'bg-green-100 text-green-800 border-green-200';
      case 'REIMBURSEMENT': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (!queueEntry) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-blue-600" />
            {isEditMode ? 'Edit IPD Admission Form' : 'IPD Admission Form'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isEditMode
              ? 'Edit ward, doctor, and billing details for an admitted patient.'
              : 'Complete admission details to move a queued patient to admitted.'}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
        {loadingAdmission ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
            Loading admission details…
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {canSetAdmissionDateTime && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Admission date &amp; time</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-w-sm space-y-2">
                  <Label htmlFor="admissionDateTime" className="text-sm font-medium text-gray-700">
                    Date and time of admission
                  </Label>
                  <Input
                    id="admissionDateTime"
                    type="datetime-local"
                    value={admissionDateTime}
                    onChange={(e) => setAdmissionDateTime(e.target.value)}
                    disabled={isSubmitting}
                  />
                  <p className="text-xs text-muted-foreground">
                    {isEditMode
                      ? 'Defaults to the original admission time. Change only if the recorded date/time needs correction.'
                      : 'Leave blank to use the current date and time when admitting.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
          {/* Patient Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="h-4 w-4" />
                Patient Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Patient Name</Label>
                  <div className="p-3 bg-gray-50 rounded-md border">
                    <span className="font-medium">{queueEntry.patient.name}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">UHID</Label>
                  <div className="p-3 bg-gray-50 rounded-md border">
                    <span className="font-mono text-sm">{queueEntry.patient.uhid}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Phone</Label>
                  <div className="p-3 bg-gray-50 rounded-md border">
                    <span>{maskPhoneDisplay(queueEntry.patient.phone)}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Gender</Label>
                  <div className="p-3 bg-gray-50 rounded-md border">
                    <span>{queueEntry.patient.gender}</span>
                  </div>
                </div>
              </div>

              {/* Referrer name (stored against patient) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="referralPersonName" className="text-sm font-medium text-gray-700">
                    Referrer Name
                  </Label>
                  <div className="flex items-center gap-2">
                    <SearchableSelect
                      id="referralPersonName"
                      value={referrerSelectValue}
                      onValueChange={(v) => {
                        if (v === '__add__') {
                          setAddingReferrerName(true);
                          return;
                        }
                        handleInputChange('referralPersonName', v);
                      }}
                      options={referrerOptions}
                      placeholder="Select referrer (optional)"
                      searchPlaceholder="Type doctor name…"
                      emptyMessage="No doctors match your search."
                    />
                    {formData.referralPersonName ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleInputChange('referralPersonName', '')}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Sales Person</Label>
                  <div className="flex items-center gap-2">
                    <Select
                      value={formData.salesPersonName || ''}
                      onValueChange={(v) => {
                        if (v === '__add__') {
                          setAddingSalesName(true);
                          return;
                        }
                        handleInputChange('salesPersonName', v);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select sales person (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {salesOptions.map((n) => (
                          <SelectItem key={n} value={n}>
                            {n}
                          </SelectItem>
                        ))}
                        <SelectItem value="__add__">+ Add name…</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.salesPersonName ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleInputChange('salesPersonName', '')}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="leadSource" className="text-sm font-medium text-gray-700">
                    Lead Source{needsRegistrationSourceCapture ? ' *' : ''}
                  </Label>
                  {needsRegistrationSourceCapture ? (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-1 mb-2">
                      This patient self-registered online. Please record how they heard about the hospital.
                    </p>
                  ) : null}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <LeadSourceSearchableSelect
                      id="leadSource"
                      value={formData.leadSource || ''}
                      onValueChange={(v) => {
                        handleInputChange('leadSource', v);
                        if (!leadSourceNeedsDetails(v)) {
                          handleInputChange('leadSourceDetails', '');
                        }
                      }}
                      extraApiSources={leadSourceApiSources}
                      placeholder={
                        needsRegistrationSourceCapture
                          ? 'Select lead source'
                          : 'Select lead source (optional)'
                      }
                      searchPlaceholder="Search lead source…"
                      className="flex-1 min-w-0"
                    />
                    {formData.leadSource && !needsRegistrationSourceCapture ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          handleInputChange('leadSource', '');
                          handleInputChange('leadSourceDetails', '');
                        }}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </div>
                </div>
                {leadSourceNeedsDetails(formData.leadSource) ? (
                  <div>
                    <Label htmlFor="leadSourceDetails" className="text-sm font-medium text-gray-700">
                      {formData.leadSource === 'REFERRAL'
                        ? 'Referrer details'
                        : formData.leadSource === 'AFFILIATE'
                          ? 'Affiliate details'
                          : 'Custom source details'}
                    </Label>
                    <Input
                      id="leadSourceDetails"
                      value={formData.leadSourceDetails || ''}
                      onChange={(e) => handleInputChange('leadSourceDetails', e.target.value)}
                      placeholder={
                        formData.leadSource === 'CUSTOM'
                          ? 'e.g. Meta paid, Google paid…'
                          : 'Optional details'
                      }
                    />
                  </div>
                ) : (
                  <div className="hidden md:block" aria-hidden />
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Partner lead</Label>
                  <div className="flex items-center gap-2">
                    <Select
                      value={formData.partnerLeadName || ''}
                      onValueChange={(v) => {
                        if (v === '__add__') {
                          setAddingPartnerName(true);
                          return;
                        }
                        handleInputChange('partnerLeadName', v);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select partner lead (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {partnerOptions.map((n) => (
                          <SelectItem key={n} value={n}>
                            {n}
                          </SelectItem>
                        ))}
                        <SelectItem value="__add__">+ Add partner…</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.partnerLeadName ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleInputChange('partnerLeadName', '')}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Financing Partner</Label>
                  <div className="flex items-center gap-2">
                    <Select
                      value={formData.financingPartnerName || ''}
                      onValueChange={(v) => {
                        if (v === '__add__') {
                          setAddingFinancingPartnerName(true);
                          return;
                        }
                        handleInputChange('financingPartnerName', v);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select financing partner (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {financingPartnerOptions.map((n) => (
                          <SelectItem key={n} value={n}>
                            {n}
                          </SelectItem>
                        ))}
                        <SelectItem value="__add__">+ Add partner…</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.financingPartnerName ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleInputChange('financingPartnerName', '')}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Third Party Hospital</Label>
                  <div className="flex items-center gap-2">
                    <Select
                      value={formData.thirdPartyHospitalName || ''}
                      onValueChange={(v) => {
                        if (v === '__add__') {
                          setAddingThirdPartyHospitalName(true);
                          return;
                        }
                        handleInputChange('thirdPartyHospitalName', v);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select third party hospital (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {thirdPartyHospitalOptions.map((n) => (
                          <SelectItem key={n} value={n}>
                            {n}
                          </SelectItem>
                        ))}
                        <SelectItem value="__add__">+ Add hospital…</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.thirdPartyHospitalName ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleInputChange('thirdPartyHospitalName', '')}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </div>
                  {isThirdPartyStay ? (
                    <p className="mt-1 text-xs text-amber-700">
                      Surgery/stay at external hospital — no ward or bed will be blocked here. Pharma and lab still run at this hospital.
                    </p>
                  ) : null}
                </div>
                <div>
                  <Label htmlFor="admissionCareType" className="text-sm font-medium text-gray-700">
                    Surgery or medical management
                  </Label>
                  <div className="flex items-center gap-2">
                    <Select
                      value={formData.admissionCareType || ''}
                      onValueChange={(v) => handleInputChange('admissionCareType', v)}
                    >
                      <SelectTrigger id="admissionCareType">
                        <SelectValue placeholder="Select (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SURGERY">Surgery</SelectItem>
                        <SelectItem value="MEDICAL_MANAGEMENT">Medical management</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.admissionCareType ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleInputChange('admissionCareType', '')}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Dialog open={addingFinancingPartnerName} onOpenChange={setAddingFinancingPartnerName}>
            <DialogContent className="sm:max-w-[420px]">
              <DialogHeader>
                <DialogTitle>Add Financing Partner</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Name</Label>
                  <Input
                    value={newFinancingPartnerName}
                    onChange={(e) => setNewFinancingPartnerName(e.target.value)}
                    placeholder="Enter financing partner name"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setAddingFinancingPartnerName(false);
                      setNewFinancingPartnerName('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      const nm = newFinancingPartnerName.trim();
                      if (!nm) return;
                      const next = Array.from(new Set([...financingPartnerOptions, nm]));
                      setFinancingPartnerOptions(next);
                      try {
                        const extra = next.filter((x) => !DEFAULT_FINANCING_PARTNER_NAMES.includes(x));
                        localStorage.setItem(
                          FINANCING_PARTNER_DROPDOWN_STORAGE_KEY,
                          JSON.stringify(extra),
                        );
                      } catch {}
                      handleInputChange('financingPartnerName', nm);
                      setNewFinancingPartnerName('');
                      setAddingFinancingPartnerName(false);
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={addingThirdPartyHospitalName} onOpenChange={setAddingThirdPartyHospitalName}>
            <DialogContent className="sm:max-w-[420px]">
              <DialogHeader>
                <DialogTitle>Add Third Party Hospital</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Name</Label>
                  <Input
                    value={newThirdPartyHospitalName}
                    onChange={(e) => setNewThirdPartyHospitalName(e.target.value)}
                    placeholder="Enter third party hospital name"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setAddingThirdPartyHospitalName(false);
                      setNewThirdPartyHospitalName('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      const nm = newThirdPartyHospitalName.trim();
                      if (!nm) return;
                      const next = Array.from(new Set([...thirdPartyHospitalOptions, nm]));
                      setThirdPartyHospitalOptions(next);
                      try {
                        localStorage.setItem(
                          THIRD_PARTY_HOSPITAL_DROPDOWN_STORAGE_KEY,
                          JSON.stringify(next),
                        );
                      } catch {}
                      handleInputChange('thirdPartyHospitalName', nm);
                      setNewThirdPartyHospitalName('');
                      setAddingThirdPartyHospitalName(false);
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={addingPartnerName} onOpenChange={setAddingPartnerName}>
            <DialogContent className="sm:max-w-[420px]">
              <DialogHeader>
                <DialogTitle>Add Partner</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Name</Label>
                  <Input
                    value={newPartnerName}
                    onChange={(e) => setNewPartnerName(e.target.value)}
                    placeholder="Enter partner name"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setAddingPartnerName(false);
                      setNewPartnerName('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      const nm = newPartnerName.trim();
                      if (!nm) return;
                      const next = Array.from(new Set([...partnerOptions, nm]));
                      setPartnerOptions(next);
                      try {
                        const extra = next.filter((x) => !DEFAULT_PARTNER_NAMES.includes(x));
                        localStorage.setItem(PARTNER_DROPDOWN_STORAGE_KEY, JSON.stringify(extra));
                      } catch {}
                      handleInputChange('partnerLeadName', nm);
                      setNewPartnerName('');
                      setAddingPartnerName(false);
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={addingReferrerName} onOpenChange={setAddingReferrerName}>
            <DialogContent className="sm:max-w-[420px]">
              <DialogHeader>
                <DialogTitle>Add Referrer</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Name</Label>
                  <Input
                    value={newReferrerName}
                    onChange={(e) => setNewReferrerName(e.target.value)}
                    placeholder="Enter referrer name"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setAddingReferrerName(false);
                      setNewReferrerName('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      const nm = newReferrerName.trim();
                      if (!nm) return;
                      const next = Array.from(new Set([...customReferrerOptions, nm]));
                      setCustomReferrerOptions(next);
                      try {
                        localStorage.setItem(REFERRER_DROPDOWN_STORAGE_KEY, JSON.stringify(next));
                      } catch {}
                      handleInputChange('referralPersonName', nm);
                      setNewReferrerName('');
                      setAddingReferrerName(false);
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={addingSalesName} onOpenChange={setAddingSalesName}>
            <DialogContent className="sm:max-w-[420px]">
              <DialogHeader>
                <DialogTitle>Add Sales Person</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Name</Label>
                  <Input value={newSalesName} onChange={(e) => setNewSalesName(e.target.value)} placeholder="Enter name" />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => { setAddingSalesName(false); setNewSalesName(''); }}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      const nm = newSalesName.trim();
                      if (!nm) return;
                      const next = Array.from(new Set([...salesOptions, nm]));
                      setSalesOptions(next);
                      try {
                        const extra = next.filter((x) => !DEFAULT_SALES_NAMES.includes(x));
                        localStorage.setItem(SALES_DROPDOWN_STORAGE_KEY, JSON.stringify(extra));
                      } catch {}
                      handleInputChange('salesPersonName', nm);
                      setNewSalesName('');
                      setAddingSalesName(false);
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Doctor Assignment */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Stethoscope className="h-4 w-4" />
                Doctor Assignment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="assignedDoctorId" className="text-sm font-medium text-gray-700">
                    Assigned Doctor <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center gap-2">
                  <DoctorSearchableSelect
                    doctors={doctors}
                    value={formData.assignedDoctorId}
                    onValueChange={(value) => handleInputChange('assignedDoctorId', value)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setAddDoctorOpen(true)}
                    className="whitespace-nowrap"
                  >
                    + Add doctor
                  </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Insurance Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Shield className="h-4 w-4" />
                Insurance Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="insuranceType" className="text-sm font-medium text-gray-700">
                    Insurance Type <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.insuranceType}
                    onValueChange={(value) => handleInputChange('insuranceType', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select insurance type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NA">No Insurance</SelectItem>
                      <SelectItem value="CASHLESS">Cashless</SelectItem>
                      <SelectItem value="REIMBURSEMENT">Reimbursement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.insuranceType !== 'NA' && (
                  <>
                    <div>
                      <Label htmlFor="insuranceCompany" className="text-sm font-medium text-gray-700">
                        Insurance Company
                      </Label>
                      <Select
                        value={formData.insuranceCompany || 'none'}
                        onValueChange={(value) => handleInputChange('insuranceCompany', value === 'none' ? '' : value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select insurance company" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Select company</SelectItem>
                          {insuranceCompanies.map((company) => (
                            <SelectItem key={company.id} value={company.name}>
                              <div className="flex items-center gap-2">
                                <span>{company.name}</span>
                                {company.isPartnered && (
                                  <Badge variant="secondary" className="text-xs">
                                    Partnered
                                  </Badge>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="policyNumber" className="text-sm font-medium text-gray-700">
                        Policy Number
                      </Label>
                      <Input
                        id="policyNumber"
                        value={formData.policyNumber || ''}
                        onChange={(e) => handleInputChange('policyNumber', e.target.value)}
                        placeholder="Enter policy number"
                      />
                    </div>

                    <div>
                      <Label htmlFor="insuranceNumber" className="text-sm font-medium text-gray-700">
                        Insurance Number
                      </Label>
                      <Input
                        id="insuranceNumber"
                        value={formData.insuranceNumber || ''}
                        onChange={(e) => handleInputChange('insuranceNumber', e.target.value)}
                        placeholder="Enter insurance number"
                      />
                    </div>

                    <div>
                      <Label htmlFor="tpaName" className="text-sm font-medium text-gray-700">
                        TPA Name
                      </Label>
                      <Input
                        id="tpaName"
                        value={formData.tpaName || ''}
                        onChange={(e) => handleInputChange('tpaName', e.target.value)}
                        placeholder="Enter TPA name"
                      />
                    </div>

                    <div>
                      <Label htmlFor="insuranceCard" className="text-sm font-medium text-gray-700">
                        Insurance Card Document
                      </Label>
                      <div className="space-y-2">
                        {!insuranceCardFile ? (
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                            <input
                              type="file"
                              id="insuranceCard"
                              accept="image/*,.pdf"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  setInsuranceCardFile(file);
                                }
                              }}
                              className="hidden"
                            />
                            <label
                              htmlFor="insuranceCard"
                              className="cursor-pointer flex flex-col items-center gap-2"
                            >
                              <Upload className="h-8 w-8 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                Click to upload insurance card
                              </span>
                              <span className="text-xs text-gray-500">
                                Supports: JPG, PNG, PDF
                              </span>
                            </label>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-green-600" />
                              <span className="text-sm font-medium text-green-800">
                                {insuranceCardFile.name}
                              </span>
                              <span className="text-xs text-green-600">
                                ({(insuranceCardFile.size / 1024).toFixed(1)} KB)
                              </span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setInsuranceCardFile(null)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Ward Assignment — hidden for third-party hospital stays */}
          {!isThirdPartyStay ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building className="h-4 w-4" />
                Ward Assignment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="wardType" className="text-sm font-medium text-gray-700">
                    Ward Type <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.wardType}
                    onValueChange={(value) => handleInputChange('wardType', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select ward type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GENERAL">General</SelectItem>
                      <SelectItem value="ICU">ICU</SelectItem>
                      <SelectItem value="PRIVATE">Private</SelectItem>
                      <SelectItem value="SEMI_PRIVATE">Semi-Private</SelectItem>
                      <SelectItem value="EMERGENCY">Emergency</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="wardSubType" className="text-sm font-medium text-gray-700">
                    Ward Sub Type
                  </Label>
                  <Select
                    value={formData.wardSubType || 'none'}
                    onValueChange={(value) => handleInputChange('wardSubType', value === 'none' ? undefined : value as WardSubType)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select ward sub type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value={WardSubType.AC}>AC</SelectItem>
                      <SelectItem value={WardSubType.NON_AC}>Non-AC</SelectItem>
                      <SelectItem value={WardSubType.SINGLE}>Single</SelectItem>
                      <SelectItem value={WardSubType.DOUBLE}>Double</SelectItem>
                      <SelectItem value={WardSubType.TRIPLE}>Triple</SelectItem>
                      <SelectItem value={WardSubType.QUADRUPLE}>Quadruple</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="wardId" className="text-sm font-medium text-gray-700">
                    Select Ward <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.wardId || 'none'}
                    onValueChange={(value) => handleInputChange('wardId', value === 'none' ? '' : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a ward" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select ward</SelectItem>
                      {wards
                        .filter(ward => {
                          // Filter by ward type
                          if (ward.type !== formData.wardType) return false;
                          
                          // Filter by ward sub type if specified
                          if (formData.wardSubType && ward.subType !== formData.wardSubType) return false;
                          
                          return true;
                        })
                        .map((ward) => (
                          <SelectItem key={ward.id} value={ward.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{ward.name}</span>
                              <span className="text-sm text-gray-500">
                                {ward.availableBeds} of {ward.totalBeds} beds available
                                {ward.subType && ` • ${ward.subType}`}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Bed Selection Grid */}
              {selectedWard && beds.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Select Bed - {selectedWard.name}
                  </Label>
                  <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                    {beds.map((bed) => (
                      <div
                        key={bed.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          formData.bedId === bed.id
                            ? 'bg-blue-100 border-blue-300 text-blue-800'
                            : 'bg-green-100 border-green-300 text-green-800 hover:bg-green-200'
                        }`}
                        onClick={() => {
                          handleInputChange('bedId', bed.id);
                          handleInputChange('bedNumber', bed.bedNumber);
                        }}
                      >
                        <div className="text-center">
                          <div className="font-medium text-sm">Bed {bed.bedNumber}</div>
                          {bed.pricePerDay && (
                            <div className="text-xs text-gray-600">₹{bed.pricePerDay}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
                      <span>Available</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></div>
                      <span>Selected</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Manual Room/Bed Entry */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="roomNumber" className="text-sm font-medium text-gray-700">
                    Room Number
                  </Label>
                  <Input
                    id="roomNumber"
                    value={formData.roomNumber || ''}
                    onChange={(e) => handleInputChange('roomNumber', e.target.value)}
                    placeholder="Enter room number"
                  />
                </div>

                <div>
                  <Label htmlFor="bedNumber" className="text-sm font-medium text-gray-700">
                    Bed Number
                  </Label>
                  <Input
                    id="bedNumber"
                    value={formData.bedNumber || ''}
                    onChange={(e) => handleInputChange('bedNumber', e.target.value)}
                    placeholder="Enter bed number"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          ) : (
            <Card>
              <CardContent className="py-4">
                <p className="text-sm text-amber-800">
                  <span className="font-medium">Third party:</span>{' '}
                  {formData.thirdPartyHospitalName}. Ward and bed assignment are skipped for this admission.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Package / Surgery (optional) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="h-4 w-4" />
                Package & Surgery (optional)
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Package patients are billed at the package amount (room charges excluded). Choose what is
                included in the package; excluded items are billed separately. Tick GST below when 5% tax
                applies (e.g. plastic surgery).
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {packageSurgeryRows.map((row, index) => (
                  <div
                    key={row.key}
                    className="rounded-md border border-slate-200 bg-slate-50/50 p-3 space-y-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Surgery {index + 1}
                      </p>
                      {packageSurgeryRows.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 text-rose-600 hover:text-rose-700"
                          disabled={isSubmitting}
                          onClick={() =>
                            setPackageSurgeryRows((prev) => prev.filter((r) => r.key !== row.key))
                          }
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          Remove
                        </Button>
                      )}
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Specialisation</Label>
                      <SearchableSelect
                        id={`packageSpecialisation-${row.key}`}
                        value={row.specialisation}
                        onValueChange={(v) => {
                          if (v === '__add__') {
                            setAddingPackageSpecialisationForKey(row.key);
                            return;
                          }
                          setPackageSurgeryRows((prev) =>
                            prev.map((r) => {
                              if (r.key !== row.key) return r;
                              const surgeries = getSurgeriesForSpecialisation(v, customSurgeriesBySpec);
                              return {
                                ...r,
                                specialisation: v,
                                surgeryName:
                                  r.surgeryName && !surgeries.includes(r.surgeryName)
                                    ? ''
                                    : r.surgeryName,
                              };
                            })
                          );
                        }}
                        options={packageSpecialisationOptions}
                        placeholder="Select specialisation (optional)"
                        searchPlaceholder="Type specialisation…"
                        emptyMessage="No specialisation matches."
                        disabled={isSubmitting}
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Surgery name</Label>
                      <SearchableSelect
                        id={`surgeryName-${row.key}`}
                        value={row.surgeryName}
                        onValueChange={(v) => {
                          if (v === '__add__') {
                            if (!row.specialisation) {
                              toast.error('Select a specialisation first');
                              return;
                            }
                            setAddingPackageSurgeryForKey(row.key);
                            return;
                          }
                          setPackageSurgeryRows((prev) =>
                            prev.map((r) => (r.key === row.key ? { ...r, surgeryName: v } : r))
                          );
                        }}
                        options={surgeryOptionsForSpec(row.specialisation)}
                        placeholder={
                          row.specialisation
                            ? 'Select surgery (optional)'
                            : 'Select specialisation first'
                        }
                        searchPlaceholder="Type surgery name…"
                        emptyMessage={
                          row.specialisation
                            ? 'No surgery matches. Use + Add surgery…'
                            : 'Select specialisation first.'
                        }
                        disabled={isSubmitting || !row.specialisation}
                      />
                    </div>
                  </div>
                ))}
                {packageSurgeryRows.length < 3 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    disabled={isSubmitting}
                    onClick={() =>
                      setPackageSurgeryRows((prev) => [
                        ...prev,
                        { key: newPackageSurgeryRowKey(), specialisation: '', surgeryName: '' },
                      ])
                    }
                  >
                    <Plus className="h-4 w-4" />
                    Add another surgery
                  </Button>
                )}
              </div>
              <div>
                <Label htmlFor="totalPackageCost" className="text-sm font-medium text-gray-700">
                  Total package cost (₹)
                </Label>
                <Input
                  id="totalPackageCost"
                  type="number"
                  min={0}
                  step={0.01}
                  value={formData.totalPackageCost ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    handleInputChange('totalPackageCost', v === '' ? '' : v);
                    if (v === '') {
                      handleInputChange('packageType', undefined as unknown as IPDPackageType);
                    } else if (!formData.packageType) {
                      handleInputChange('packageType', 'PACKAGE_1');
                    }
                  }}
                  placeholder="Leave empty if not a package patient"
                />
              </div>
              {formData.totalPackageCost !== '' && Number(formData.totalPackageCost) > 0 && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Package type</Label>
                  <Select
                    value={formData.packageType ?? 'PACKAGE_1'}
                    onValueChange={(v: IPDPackageType) => handleInputChange('packageType', v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select package type" />
                    </SelectTrigger>
                    <SelectContent>
                      {IPD_PACKAGE_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {IPD_PACKAGE_TYPE_OPTIONS.find((o) => o.value === (formData.packageType ?? 'PACKAGE_1'))
                      ?.description}
                  </p>
                </div>
              )}
              <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50/80 p-4">
                <Checkbox
                  id="gstApplicable"
                  checked={!!formData.gstApplicable}
                  onCheckedChange={(checked) =>
                    handleInputChange('gstApplicable', checked === true)
                  }
                  disabled={isSubmitting}
                />
                <div className="space-y-1">
                  <Label htmlFor="gstApplicable" className="text-sm font-medium cursor-pointer">
                    GST applicable (5%)
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Adds 5% GST on the final IPD bill. GSTIN on the bill: 06ABCCA6081B1Z3. Use for
                    plastic surgery and other taxable procedures when required.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Medical Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="h-4 w-4" />
                Medical Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="chiefComplaint" className="text-sm font-medium text-gray-700">
                  Chief Complaint
                </Label>
                <Textarea
                  id="chiefComplaint"
                  value={formData.chiefComplaint || ''}
                  onChange={(e) => handleInputChange('chiefComplaint', e.target.value)}
                  placeholder="Enter chief complaint"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="admissionNotes" className="text-sm font-medium text-gray-700">
                  Admission Notes
                </Label>
                <Textarea
                  id="admissionNotes"
                  value={formData.admissionNotes || ''}
                  onChange={(e) => handleInputChange('admissionNotes', e.target.value)}
                  placeholder="Enter admission notes"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Form Summary */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900">Admission Summary</h4>
                  <div className="mt-2 space-y-1 text-sm text-blue-800">
                    <div>• Patient: <span className="font-medium">{queueEntry.patient.name}</span></div>
                    <div>• Insurance: <Badge className={getInsuranceTypeColor(formData.insuranceType)}>
                      {formData.insuranceType}
                    </Badge></div>
                    {formData.insuranceNumber && <div>• Insurance Number: <span className="font-medium">{formData.insuranceNumber}</span></div>}
                    {insuranceCardFile && <div>• Insurance Card: <span className="font-medium text-green-600">Uploaded</span></div>}
                    <div>• Ward: <Badge className={getWardTypeColor(formData.wardType)}>
                      {formData.wardType}
                    </Badge></div>
                    {formData.wardSubType && <div>• Sub Type: <span className="font-medium">{formData.wardSubType}</span></div>}
                    {selectedWard && <div>• Ward: <span className="font-medium">{selectedWard.name}</span></div>}
                    {formData.bedNumber && <div>• Bed: <span className="font-medium">{formData.bedNumber}</span></div>}
                    {packageSurgeryRows
                      .filter((r) => r.surgeryName.trim() || r.specialisation.trim())
                      .map((r, i) => (
                        <div key={r.key}>
                          • Surgery {i + 1}:{' '}
                          <span className="font-medium">
                            {[r.specialisation, r.surgeryName].filter(Boolean).join(' — ') || '—'}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                loadingAdmission ||
                !formData.assignedDoctorId ||
                (!isThirdPartyStay && (!formData.wardType || (!isEditMode && !formData.wardId)))
              }
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isEditMode ? 'Saving…' : 'Admitting...'}
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {isEditMode ? 'Save changes' : 'Admit Patient'}
                </>
              )}
            </Button>
          </div>
        </form>
        )}
        </div>
      </DialogContent>

      <Dialog
        open={!!addingPackageSpecialisationForKey}
        onOpenChange={(open) => {
          if (!open) {
            setAddingPackageSpecialisationForKey(null);
            setNewPackageSpecialisation('');
          }
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Add specialisation</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium text-gray-700">Specialisation name</Label>
              <Input
                value={newPackageSpecialisation}
                onChange={(e) => setNewPackageSpecialisation(e.target.value)}
                placeholder="e.g. Bariatric Surgery"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setAddingPackageSpecialisationForKey(null);
                  setNewPackageSpecialisation('');
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  const nm = newPackageSpecialisation.trim();
                  const rowKey = addingPackageSpecialisationForKey;
                  if (!nm || !rowKey) return;
                  const extra = Array.from(new Set([...customPackageSpecialisations, nm]));
                  setCustomPackageSpecialisations(extra);
                  persistCustomPackageSpecialisations(extra);
                  setPackageSurgeryRows((prev) =>
                    prev.map((r) =>
                      r.key === rowKey ? { ...r, specialisation: nm, surgeryName: '' } : r
                    )
                  );
                  setNewPackageSpecialisation('');
                  setAddingPackageSpecialisationForKey(null);
                }}
              >
                Add
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!addingPackageSurgeryForKey}
        onOpenChange={(open) => {
          if (!open) {
            setAddingPackageSurgeryForKey(null);
            setNewPackageSurgery('');
          }
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Add surgery</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium text-gray-700">Surgery name</Label>
              <Input
                value={newPackageSurgery}
                onChange={(e) => setNewPackageSurgery(e.target.value)}
                placeholder="e.g. Rhinoplasty"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Added under:{' '}
              <span className="font-medium">
                {packageSurgeryRows.find((r) => r.key === addingPackageSurgeryForKey)?.specialisation ||
                  '—'}
              </span>
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setAddingPackageSurgeryForKey(null);
                  setNewPackageSurgery('');
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  const nm = newPackageSurgery.trim();
                  const rowKey = addingPackageSurgeryForKey;
                  const row = packageSurgeryRows.find((r) => r.key === rowKey);
                  if (!nm || !rowKey || !row?.specialisation) return;
                  const existing = customSurgeriesBySpec[row.specialisation] ?? [];
                  const nextForSpec = Array.from(new Set([...existing, nm]));
                  const nextAll = { ...customSurgeriesBySpec, [row.specialisation]: nextForSpec };
                  setCustomSurgeriesBySpec(nextAll);
                  persistCustomSurgeriesForSpec(row.specialisation, nextForSpec);
                  setPackageSurgeryRows((prev) =>
                    prev.map((r) => (r.key === rowKey ? { ...r, surgeryName: nm } : r))
                  );
                  setNewPackageSurgery('');
                  setAddingPackageSurgeryForKey(null);
                }}
              >
                Add
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={addDoctorOpen} onOpenChange={setAddDoctorOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add doctor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-700">Doctor name</Label>
              <Input
                value={newDoctorName}
                onChange={(e) => setNewDoctorName(e.target.value)}
                placeholder="e.g. Dr. Sandeep Kumar"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Specialisation</Label>
              <Select value={newDoctorSpec || '__none__'} onValueChange={(v) => setNewDoctorSpec(v === '__none__' ? '' : v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select specialisation" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__none__">Not specified</SelectItem>
                  {MEDICAL_SPECIALISATIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAddDoctorOpen(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={handleQuickCreateDoctor} disabled={creatingDoctor}>
                {creatingDoctor ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
